import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  addressesApi,
  integrationsApi,
  type Address,
  type TripStopInput,
} from '@/api/client';
import { useDebounce } from '@/hooks';

export type AddressSelection =
  | { kind: 'existing'; id: string; display: string }
  | { kind: 'custom'; fullAddress: string; label: string; saveToBook: boolean };

export function emptyAddressSelection(): AddressSelection | null {
  return null;
}

export function addressSelectionDisplay(value: AddressSelection | null): string {
  if (!value) return '';
  if (value.kind === 'existing') return value.display;
  return value.fullAddress;
}

export function isAddressSelectionReady(value: AddressSelection | null): boolean {
  if (!value) return false;
  if (value.kind === 'existing') return Boolean(value.id);
  return value.fullAddress.trim().length >= 3;
}

/** Resolve a picker value into trip-stop fields, optionally creating an address book entry. */
export async function resolveAddressSelection(
  selection: AddressSelection,
  clientId: string,
  onCreated?: (address: Address) => void,
): Promise<Pick<TripStopInput, 'addressBookEntryId' | 'latitude' | 'longitude' | 'zoneId'>> {
  if (selection.kind === 'existing') {
    return { addressBookEntryId: selection.id };
  }

  const fullAddress = selection.fullAddress.trim();
  const label = (selection.label.trim() || fullAddress.slice(0, 48)).trim();

  if (selection.saveToBook) {
    const created = await addressesApi.create({
      clientId,
      label,
      fullAddress,
    });
    onCreated?.(created);
    return { addressBookEntryId: created.id };
  }

  const geo = await integrationsApi.geocode(fullAddress);
  return {
    latitude: geo.latitude,
    longitude: geo.longitude,
    zoneId: geo.zoneId ?? undefined,
  };
}

type StopAddressPickerProps = {
  label: string;
  clientId?: string | null;
  addresses: Address[];
  value: AddressSelection | null;
  onChange: (value: AddressSelection | null) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  /** When true, custom addresses can be saved to the book (default true). */
  allowSaveToBook?: boolean;
};

function formatAddress(a: Address): string {
  return `${a.label} — ${a.fullAddress}`;
}

export function StopAddressPicker({
  label,
  clientId,
  addresses,
  value,
  onChange,
  disabled = false,
  required = false,
  placeholder = 'Search or type an address…',
  allowSaveToBook = true,
}: StopAddressPickerProps) {
  const listId = useId();
  const inputId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(() => addressSelectionDisplay(value));
  const [saveToBook, setSaveToBook] = useState(
    () => (value?.kind === 'custom' ? value.saveToBook : false),
  );
  const [saveLabel, setSaveLabel] = useState(
    () => (value?.kind === 'custom' ? value.label : ''),
  );

  const debouncedQuery = useDebounce(query, 200);

  // Keep local query in sync when parent clears / resets the value
  useEffect(() => {
    const display = addressSelectionDisplay(value);
    setQuery(display);
    if (value?.kind === 'custom') {
      setSaveToBook(value.saveToBook);
      setSaveLabel(value.label);
    }
  }, [value]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return addresses.slice(0, 40);
    return addresses
      .filter(
        (a) =>
          a.label.toLowerCase().includes(q) ||
          a.fullAddress.toLowerCase().includes(q),
      )
      .slice(0, 40);
  }, [addresses, debouncedQuery]);

  const exactExisting = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return (
      addresses.find(
        (a) =>
          formatAddress(a).toLowerCase() === q ||
          a.fullAddress.toLowerCase() === q ||
          a.label.toLowerCase() === q,
      ) ?? null
    );
  }, [addresses, query]);

  const showUseTyped =
    query.trim().length >= 3 &&
    !exactExisting &&
    !(value?.kind === 'existing' && addressSelectionDisplay(value) === query.trim());

  const emitCustom = (fullAddress: string, nextSave = saveToBook, nextLabel = saveLabel) => {
    const trimmed = fullAddress.trim();
    if (!trimmed) {
      onChange(null);
      return;
    }
    onChange({
      kind: 'custom',
      fullAddress: trimmed,
      label: nextLabel.trim(),
      saveToBook: allowSaveToBook ? nextSave : false,
    });
  };

  const selectExisting = (a: Address) => {
    const display = formatAddress(a);
    setQuery(display);
    setOpen(false);
    onChange({ kind: 'existing', id: a.id, display });
  };

  const selectTyped = () => {
    setOpen(false);
    emitCustom(query);
  };

  const onInputChange = (next: string) => {
    setQuery(next);
    setOpen(true);
    const match = addresses.find((a) => formatAddress(a) === next);
    if (match) {
      onChange({ kind: 'existing', id: match.id, display: formatAddress(match) });
      return;
    }
    if (next.trim().length >= 3) {
      emitCustom(next);
    } else {
      onChange(null);
    }
  };

  const isCustom = value?.kind === 'custom';

  return (
    <div className={`stop-address-picker ${disabled ? 'disabled' : ''}`} ref={wrapRef}>
      <label className="sap-label" htmlFor={inputId}>
        {label}
        {required && <span className="input-required" aria-hidden="true"> *</span>}
      </label>
      <div className="sap-control">
        <input
          id={inputId}
          type="text"
          className="sap-input"
          value={query}
          disabled={disabled || !clientId}
          placeholder={!clientId ? 'Select a client first' : placeholder}
          required={required}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          onFocus={() => !disabled && clientId && setOpen(true)}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter' && open && showUseTyped && filtered.length === 0) {
              e.preventDefault();
              selectTyped();
            }
          }}
        />
        {open && clientId && !disabled && (
          <ul id={listId} className="sap-list" role="listbox">
            {filtered.map((a) => (
              <li key={a.id} role="option">
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => selectExisting(a)}>
                  <strong>{a.label}</strong>
                  <span>{a.fullAddress}</span>
                </button>
              </li>
            ))}
            {showUseTyped && (
              <li role="option" className="sap-use-typed">
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={selectTyped}>
                  <strong>Use typed address</strong>
                  <span>{query.trim()}</span>
                </button>
              </li>
            )}
            {!filtered.length && !showUseTyped && (
              <li className="sap-empty">No matching saved addresses — keep typing a new one</li>
            )}
          </ul>
        )}
      </div>

      {isCustom && allowSaveToBook && (
        <div className="sap-save">
          <label className="sap-save-check">
            <input
              type="checkbox"
              checked={saveToBook}
              onChange={(e) => {
                const next = e.target.checked;
                setSaveToBook(next);
                emitCustom(query, next, saveLabel);
              }}
            />
            Save to address book
          </label>
          {saveToBook && (
            <input
              type="text"
              className="sap-label-input"
              aria-label={`Address book label for ${label}`}
              value={saveLabel}
              placeholder="Label (e.g. Warehouse A)"
              onChange={(e) => {
                setSaveLabel(e.target.value);
                emitCustom(query, true, e.target.value);
              }}
            />
          )}
        </div>
      )}

      <style>{`
        .stop-address-picker {
          margin-bottom: var(--space-md);
          flex: 1;
          min-width: 0;
        }
        .stop-address-picker.disabled { opacity: 0.65; }
        .sap-label {
          display: block;
          font-weight: 500;
          margin-bottom: var(--space-xs);
          font-size: 0.875rem;
        }
        .sap-label .input-required { color: var(--color-urgent); }
        .sap-control { position: relative; }
        .sap-input {
          width: 100%;
          padding: var(--space-sm) var(--space-md);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          background: var(--color-surface);
          color: var(--color-text);
          font-size: 1rem;
        }
        .sap-input:focus {
          outline: none;
          border-color: var(--color-primary, var(--color-signal));
        }
        .sap-list {
          position: absolute;
          z-index: 20;
          top: calc(100% + 2px);
          left: 0;
          right: 0;
          margin: 0;
          padding: 0;
          list-style: none;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
          max-height: 240px;
          overflow-y: auto;
        }
        .sap-list li button {
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
          width: 100%;
          text-align: left;
          padding: var(--space-sm) var(--space-md);
          border: 0;
          background: transparent;
          cursor: pointer;
          font-size: 0.875rem;
          color: var(--color-text);
        }
        .sap-list li button:hover,
        .sap-list li button:focus {
          background: var(--color-sand, #f5f2eb);
          outline: none;
        }
        .sap-list li button strong {
          font-weight: 600;
        }
        .sap-list li button span {
          color: var(--color-text-muted);
          font-size: 0.8125rem;
        }
        .sap-use-typed button strong { color: var(--color-forest, #1f4d3a); }
        .sap-empty {
          padding: var(--space-sm) var(--space-md);
          font-size: 0.8125rem;
          color: var(--color-text-muted);
        }
        .sap-save {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: var(--space-sm);
          margin-top: var(--space-xs);
        }
        .sap-save-check {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.8125rem;
          cursor: pointer;
          color: var(--color-text-muted);
        }
        .sap-label-input {
          flex: 1;
          min-width: 140px;
          padding: 0.35rem 0.6rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          font-size: 0.8125rem;
          background: var(--color-surface);
        }
      `}</style>
    </div>
  );
}
